import React, { useContext, useEffect, useState } from 'react'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import { useParams } from 'react-router-dom'

import { DEFAULT_LIMIT } from '../users/UsersViewModel'
import { deleteDashboards, getDashboards } from '../../../domain/usecases/dashboards/DashboardsUsecases'
import { ERole } from '../../../domain/interfaces/IRole'
import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'
import { IDashboard } from '../../../domain/interfaces/IDashboard'
import { ISearchResult } from '../../components/form/EdifySearch/EdifySearchDropdown'
import { showConfirmationDeleteMessage } from '../../components/dialogs/confirmation-dialog/ConfirmationDialog'
import { useGlobalProvider } from '../../../providers/GlobalProvider'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'
import usePermission from '../../../core/hooks/usePermission'
import useQuery from '../../../core/hooks/useQuery'

export interface DashboardPageContextType {
  isLoading: boolean
  error: string | undefined
  dashboards: IDashboard[]
  filteredDashboards: IDashboard[]
  title: string
  showDeleteConfirmation: (ids: string[]) => void
  loadDashboards: () => void
  sortClickedHandler: (sorter: GridSortModel) => void
  totalCount: number
  id?: string
  setSearch: (searchKey: string) => void
  loadMore: (paginationModel: GridPaginationModel) => void
  canViewDashboards: boolean
  canCreateDashboards: boolean
  canEditDashboards: boolean
  errorObj?: any | undefined
  getFavoriteCount: () => number
  queryHelper: any
  onDashboardSearch: (searchItem: ISearchResult) => void
  onDashboardFESearch: (searchKey: string) => void
  resetFilteredDashboards: () => void
  setDashboards: (dashboards: IDashboard[]) => void
  getOrgDashboards: (fetchSkip: number, query: Record<string, any>) => void
}

const DashboardPageContext = React.createContext<DashboardPageContextType | null>(null)
DashboardPageContext.displayName = 'DashboardPageContext'

interface DashboardPageVMProps {
  children: React.ReactNode
}

export const DashboardPageViewModel: React.FC<DashboardPageVMProps> = ({ children }) => {
  const { id } = useParams()
  // const { dashboardLinks } = useOrganizationProvider()
  const { isGlobalUser } = useGlobalProvider()
  const { reloadDashboardLinks } = useOrganizationProvider()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [errorObj, setErrorObj] = useState<any | undefined>(undefined)
  const [dashboards, setDashboards] = useState<IDashboard[]>([])
  const [filteredDashboards, setFilteredDashboards] = useState<IDashboard[]>([])
  const [title, setTitle] = useState<string>('Dashboards')
  const [totalCount, setTotalCount] = useState<number>(0)
  const canViewDashboards = usePermission(ERole.DashboardFeatureAccess)
  const canCreateDashboards = isGlobalUser
  const canEditDashboards = isGlobalUser

  const queryHelper = useQuery({
    skip: 0,
    limit: DEFAULT_LIMIT,
  })

  const loadMore = (paginationModel: GridPaginationModel) => {
    const fetchSkip = paginationModel.page * paginationModel.pageSize
    getOrgDashboards(fetchSkip, query)
  }

  const { query, getQueryStringFromQuery, setNew, defaultQuery } = queryHelper

  const getOrgDashboards = async (fetchSkip: number, query: Record<string, any>) => {
    setIsLoading(true)
    setError(undefined)

    const newQuery = { ...query, skip: fetchSkip }
    const res = await getDashboards(getQueryStringFromQuery(newQuery))
    setIsLoading(false)
    if (res.isLeft()) {
      setError(res.left?.message ?? 'Error loading dashboard.')
      setErrorObj(res.left)
      return
    }
    const dashboardData = res.right?.data ?? []
    setDashboards(dashboardData)
    setFilteredDashboards(dashboardData)
    setTotalCount(res.right?.total_count || 0)
    setNew(newQuery)
  }

  const showDeleteConfirmation = async (ids: string[]) => {
    const confirmMessage = ids.length > 1 ? `${ids.length} dashboards` : '1 dashboard'

    showConfirmationDeleteMessage(confirmMessage, async () => {
      const res = await deleteDashboards(ids)
      if (res.isRight()) {
        getOrgDashboards(0, defaultQuery)
        reloadDashboardLinks()

        SuccessToast({ title: `Successfully deleted ${confirmMessage}` })
        return
      }
      ErrorToast({ title: 'Failed to delete dashboards' })
    })
  }

  const sortClickedHandler = (sorter: GridSortModel) => {
    const { field, sort } = sorter[0]
    const newQuery = {
      ...query,
      skip: 0,
      sortBy: field,
      sortOrder: sort?.toUpperCase(),
    }
    getOrgDashboards(0, newQuery)
  }

  const setSearch = (searchKey: string) => {
    const newQuery = {
      ...query,
      skip: 0,
      searchKey,
    }
    getOrgDashboards(0, newQuery)
  }

  const loadDashboards = async () => {
    setError(undefined)
    setIsLoading(true)
    setDashboards([])
    getOrgDashboards(0, query)
  }

  const getFavoriteCount = () => {
    return dashboards.filter(dashboard => dashboard.isFavorited).length
  }

  const onDashboardSearch = (searchItem: ISearchResult) => {
    if (!searchItem || !searchItem.id) return
    const filtered = dashboards.filter(d => d.id === searchItem.id)
    setFilteredDashboards(filtered)
  }

  const onDashboardFESearch = (searchKey: string) => {
    if (!searchKey) {
      resetFilteredDashboards()
      return
    }

    const filtered = dashboards.filter(
      dashboard =>
        dashboard.name.toLowerCase().includes(searchKey.toLowerCase()) ||
        (dashboard.projectId && dashboard.projectId.toLowerCase().includes(searchKey.toLowerCase())) ||
        (dashboard.levelId && dashboard.levelId.toLowerCase().includes(searchKey.toLowerCase())),
    )

    setFilteredDashboards(filtered)
  }

  const resetFilteredDashboards = () => {
    setFilteredDashboards(dashboards)
  }

  useEffect(() => {
    loadDashboards()
  }, [id])

  return (
    <DashboardPageContext.Provider
      value={{
        error,
        isLoading,
        dashboards,
        filteredDashboards,
        title,
        showDeleteConfirmation,
        loadDashboards,
        sortClickedHandler,
        totalCount,
        id,
        setSearch,
        loadMore,
        canViewDashboards,
        canCreateDashboards,
        canEditDashboards,
        errorObj,
        getFavoriteCount,
        queryHelper,
        onDashboardSearch,
        onDashboardFESearch,
        resetFilteredDashboards,
        setDashboards,
        getOrgDashboards,
      }}
    >
      {children}
    </DashboardPageContext.Provider>
  )
}

export function useDashboardPageViewModel() {
  const context = useContext(DashboardPageContext)
  if (!context) {
    throw new Error('useDashboardPageViewModel must be used within a DashboardPageViewModel')
  }
  return context
}
